import { useEffect, useMemo, useState, ReactElement } from 'react';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';

import api from '~/services/api';
import Modal from '~/ui/components/common/Modal';
import Loader from '~/ui/components/common/Loader';
import NoDataFound from '~/ui/components/reusable/NoDataFound';
import PhysicianForm from './PhysicianForm';
import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import styles from './PhysiciansModal.module.scss';
import {
  IAttendingPhysician,
  IAttendingPhysicianCreate,
} from '~/services/api/attendingPhysicians/types';
import { createInitialValues } from './constants';
import PhoneFlagText from '~/ui/components/inputs/PhoneFlagText';

interface IProps {
  programId: number;
  onClose: () => void;
}

type FormMode = 'create' | 'edit' | null;

const PhysiciansModal = ({ programId, onClose }: IProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [physicians, setPhysicians] = useState<IAttendingPhysician[]>([]);
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [editablePhysician, setEditablePhysician] = useState<IAttendingPhysician>(null);

  const { showNotify, showError } = useStoreActions(actions => actions.snackbar);

  const getPhysicians = async () => {
    const data = await api.attendingPhysicians.getByProgramId(programId).then(res => res.data);
    setPhysicians(data);
  };

  const onMount = async () => {
    try {
      await getPhysicians();
    } catch (e) {
      showError(extractErrorMessage(e));
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditablePhysician(null);
    setFormMode('create');
  };

  const handleEditClick = (physician: IAttendingPhysician) => {
    setEditablePhysician(physician);
    setFormMode('edit');
  };

  const handleFormCancel = () => {
    if (isSaving) return;
    setFormMode(null);
    setEditablePhysician(null);
  };

  const handleRemove = async (id: number) => {
    try {
      setIsSaving(true);
      await api.attendingPhysicians.remove(id);
      await getPhysicians();

      if (editablePhysician?.id === id) {
        handleFormCancel();
      }

      showNotify('Physician removed');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (values: IAttendingPhysicianCreate) => {
    try {
      setIsSaving(true);

      if (formMode === 'edit' && editablePhysician) {
        await api.attendingPhysicians.update({
          ...editablePhysician,
          ...values,
        });
        showNotify('Physician updated');
      } else {
        await api.attendingPhysicians.create(programId, values);
        showNotify('Physician added');
      }

      await getPhysicians();
      handleFormCancel();
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    onMount();
  }, []);

  const formInitialValues = useMemo(
    () =>
      editablePhysician
        ? {
            name: editablePhysician.name,
            specialty: editablePhysician.specialty,
            phone: editablePhysician.phone,
            fax: editablePhysician.fax,
          }
        : createInitialValues(),
    [editablePhysician],
  );

  const footer = (
    <PhysicianForm
      showForm={Boolean(formMode)}
      initialValues={formInitialValues}
      isSaving={isSaving}
      onAddClick={handleAddClick}
      onCancel={handleFormCancel}
      onSubmit={handleSubmit}
    />
  );

  return (
    <Modal
      open
      title="Physicians"
      className={styles.modal}
      maxWidth="lg"
      onClose={onClose}
      footer={footer}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Specialization</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Fax</TableCell>
                <TableCell>Patients</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {physicians.length ? (
                physicians.map(physician => (
                  <TableRow key={physician.id}>
                    <TableCell>{physician.name}</TableCell>
                    <TableCell>{physician.specialty || '-'}</TableCell>
                    <TableCell>
                      <PhoneFlagText value={physician.phone} />
                    </TableCell>
                    <TableCell>
                      <PhoneFlagText value={physician.fax} />
                    </TableCell>
                    <TableCell>{physician.patientsCount}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        className={styles.icon}
                        title="Edit"
                        onClick={() => handleEditClick(physician)}
                      >
                        <CreateIcon />
                      </IconButton>
                      <IconButton
                        className={styles.icon}
                        title="Remove"
                        onClick={() => handleRemove(physician.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>
                    <NoDataFound />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Modal>
  );
};

export default PhysiciansModal;
